// react
import { useCallback, useEffect, useMemo, useRef } from 'react';
import * as Sentry from '@sentry/react';

// types
import {
  OrganiseDocumentStatus,
  type ChatMessage,
  type OrganiseDocument,
} from '@karehero/models';

// store
import { useDispatch } from 'store';
import { useLazyGetOrganiseDocumentQuery } from 'store/api/organiseDocument';
import { documentProcessed } from 'store/slices/onboardingV3Slice';

/** The completed event normally settles a document; polling covers a dropped stream. */
const PROCESSING_POLL_INTERVAL_MS = 15_000;

/** As the API's hand-over window: a document not read by then is not being read. */
const PROCESSING_WINDOW_MS = 10 * 60_000;

const processingDocumentIds = (messages: ChatMessage[]) =>
  messages.flatMap(({ content }) =>
    content.kind === 'file' && content.documentId && !content.isProcessed
      ? [content.documentId]
      : [],
  );

const isSettled = (doc: OrganiseDocument, now: number) =>
  !!doc.extractedAt ||
  doc.status === OrganiseDocumentStatus.Rejected ||
  now - Date.parse(doc.confirmedAt ?? doc.createdAt) > PROCESSING_WINDOW_MS;

/**
 * useProcessingDocuments checks on handed-over documents still shown as
 * processing, settling each once the server has read it or given up on it.
 */
export const useProcessingDocuments = (
  careCircleId: string | undefined,
  messages: ChatMessage[],
) => {
  const dispatch = useDispatch();
  const [getDocument] = useLazyGetOrganiseDocumentQuery();
  const pendingIds = useMemo(() => processingDocumentIds(messages), [messages]);
  // The poll reads the latest ids, so a new message alone does not restart it.
  const pendingIdsRef = useRef(pendingIds);
  useEffect(() => {
    pendingIdsRef.current = pendingIds;
  }, [pendingIds]);
  // Changes only when the set of documents does, whatever their order.
  const pendingKey = [...pendingIds].sort().join(',');

  const checkDocument = useCallback(
    (careCircleId: string, documentId: string) =>
      getDocument({ careCircleId, documentId })
        .unwrap()
        .then((doc) => {
          if (isSettled(doc, Date.now())) {
            dispatch(documentProcessed(documentId));
          }
        })
        .catch((err) => {
          Sentry.captureException(err);
          // Nothing left to check it against, so stop showing it as processing.
          dispatch(documentProcessed(documentId));
        }),
    [dispatch, getDocument],
  );

  useEffect(() => {
    if (!careCircleId || !pendingKey) return;

    const checkAll = () =>
      pendingIdsRef.current.forEach((id) => checkDocument(careCircleId, id));

    checkAll();
    const timer = setInterval(checkAll, PROCESSING_POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [careCircleId, checkDocument, pendingKey]);
};
